import React, { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';
import Mercado from '../../Mercado/Mercado';
import CardJugador from '../../CardJugador/CardJugador';
import CanchaGrid from '../../CanchaGrid/CanchaGrid';
import MenuContextual from '../../MenuContextual/MenuContextual';
import { COORDENADAS_FORMACIONES } from '../../../constants/tactics';

// EquipoLibre.js
import Tutorial from '../../../components/Tutorial/Tutorial';
import { TUTORIALES } from '../../../constants/tutorialesConfig';

const CONFIG_SUPLENTES = [
  { id: 20, pos: 'ARQ', label: 'SU' },
  { id: 21, pos: 'DEF', label: 'SD' },
  { id: 22, pos: 'VOL', label: 'SV' },
  { id: 23, pos: 'DEL', label: 'SL' },
];

const PRESUPUESTO_LIBRE = 75000000;
const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const CONFIG_SUPLENTES_LIBRE = [
  { id: 20, pos: 'ARQ', label: 'SU' },
  { id: 21, pos: 'DEF', label: 'SD' },
  { id: 22, pos: 'VOL', label: 'SV' },
  { id: 23, pos: 'DEL', label: 'SL' },
];
const FORMACIONES = ['4-3-3', '4-4-2', '3-5-2', '5-3-2', '3-4-3', '4-5-1', '5-2-3', '4-2-4', '3-3-4'];

// ── Helpers de conflicto ────────────────────────────────────────────────────

/**
 * Dado el equipo actual y dos formaciones (vieja y nueva),
 * devuelve los "conflictos": jugadores que no encajan en la nueva formación.
 *
 * Cada conflicto tiene:
 *   { slot, jugador, posNecesaria }
 *   slot        → índice en equipoLibre
 *   jugador     → objeto jugador que hay que reemplazar
 *   posNecesaria → posición que ocupa ese slot en la nueva formación
 */
const calcularConflictos = (equipoActual, formacionVieja, formacionNueva) => {
  const posVieja = COORDENADAS_FORMACIONES[formacionVieja] || [];
  const posNueva = COORDENADAS_FORMACIONES[formacionNueva] || [];

  // Cuántas posiciones de cada tipo tiene la nueva formación
  const conteoNuevo = {};
  posNueva.forEach(p => { conteoNuevo[p.pos] = (conteoNuevo[p.pos] || 0) + 1; });

  // Cuántas posiciones de cada tipo tiene la vieja formación
  const conteoViejo = {};
  posVieja.forEach(p => { conteoViejo[p.pos] = (conteoViejo[p.pos] || 0) + 1; });

  // Slots que tienen jugador
  const slotsConJugador = Object.entries(equipoActual)
    .filter(([k, v]) => Number(k) <= 10 && v)
    .map(([k, v]) => ({ slot: Number(k), jugador: v, posVieja: posVieja[Number(k)]?.pos }));

  // Contar cuántos jugadores de cada pos ya tenemos
  const jugadoresPorPos = {};
  slotsConJugador.forEach(({ posVieja: p }) => {
    if (p) jugadoresPorPos[p] = (jugadoresPorPos[p] || 0) + 1;
  });

  // Determinar excedentes: para cada posición, cuántos sobran
  const excedentes = {};
  Object.entries(jugadoresPorPos).forEach(([pos, cant]) => {
    const necesarios = conteoNuevo[pos] || 0;
    if (cant > necesarios) excedentes[pos] = cant - necesarios;
  });

  // Determinar faltantes: qué posiciones necesita la nueva formación que no tenemos suficientes
  const faltantes = [];
  Object.entries(conteoNuevo).forEach(([pos, cant]) => {
    const tenemos = jugadoresPorPos[pos] || 0;
    if (tenemos < cant) {
      for (let i = 0; i < cant - tenemos; i++) faltantes.push(pos);
    }
  });

  // Construir conflictos agrupados por posición excedente.
  // Cada conflicto tiene TODOS los jugadores excedentes de esa pos
  // para que el usuario elija cuál sacar.
  const conflictos = [];

  Object.entries(excedentes).forEach(([pos, cantExcedente]) => {
    const jugadoresExcedentes = slotsConJugador.filter(({ posVieja: p }) => p === pos);
    for (let i = 0; i < cantExcedente; i++) {
      const posNecesaria = faltantes[conflictos.length] || null;
      conflictos.push({
        posVieja: pos,
        posNecesaria,
        // Todos los candidatos a salir con esa posición
        candidatos: jugadoresExcedentes,
      });
    }
  });

  return conflictos;
};

// ── Modal de asistente de cambio de formación ───────────────────────────────
const ModalCambioFormacion = ({
  conflictos,
  conflictoActual,
  equipoTemporal,
  equipoOriginal,    // equipo ANTES de reasignar (para candidatos)
  disponible,
  onResolverConflicto,
  onCancelar,
}) => {
  const conflicto = conflictos[conflictoActual];
  const total     = conflictos.length;

  const [jugadorSaliente, setJugadorSaliente] = useState(null);
  useEffect(() => { setJugadorSaliente(null); }, [conflictoActual]);

  if (!conflicto) return null;

  const { posVieja, posNecesaria, candidatos } = conflicto;

  // Candidatos: jugadores de posVieja que aún no fueron elegidos para salir
  // en conflictos anteriores (no están en equipoTemporal con su slot original)
  const yaElegidosParaSalir = new Set(
    conflictos.slice(0, conflictoActual)
      .map(c => c._slotElegido)
      .filter(Boolean)
  );
  const candidatosActivos = candidatos.filter(c => !yaElegidosParaSalir.has(c.slot));

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.88)',
      backdropFilter: 'blur(6px)',
      zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '12px',
      overflowY: 'auto',
    }}>
      <div style={{
        background: '#0f172a',
        border: '2px solid #a855f7',
        borderRadius: '20px',
        padding: '20px',
        width: '100%',
        maxWidth: '760px',
        boxShadow: '0 0 40px rgba(168,85,247,0.25)',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <p style={{ color: '#a855f7', fontSize: '0.7rem', letterSpacing: '3px', textTransform: 'uppercase', margin: '0 0 6px' }}>
            CAMBIO DE FORMACIÓN · {conflictoActual + 1} de {total}
          </p>
          <h3 style={{ color: '#fff', margin: 0, fontSize: '1.05rem' }}>
            Sobra un <span style={{ color: '#ff4444' }}>{posVieja}</span> → elegí quién sale y quién entra
          </h3>
          <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '6px 0 0' }}>
            {!jugadorSaliente
              ? <>Seleccioná el <span style={{ color: '#ff4444', fontWeight: 'bold' }}>{posVieja}</span> que querés sacar</>
              : <>Ahora elegí el <span style={{ color: '#a855f7', fontWeight: 'bold' }}>{posNecesaria}</span> que va a entrar</>
            }
          </p>
        </div>

        {/* Dos columnas — en mobile se apilan */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 40px 1fr',
          gap: '10px',
          alignItems: 'flex-start',
        }}>

          {/* ── Izquierda: candidatos a salir ── */}
          <div style={{
            background: '#0a0f19',
            border: `1px solid ${jugadorSaliente ? '#ff444488' : '#ff444433'}`,
            borderRadius: '12px',
            padding: '12px',
            maxHeight: '320px',
            display: 'flex', flexDirection: 'column',
          }}>
            <p style={{ color: '#ff4444', fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 10px', textAlign: 'center' }}>
              ¿QUIÉN SALE? ({posVieja})
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflowY: 'auto', flex: 1 }}>
              {candidatosActivos.map(({ slot, jugador }) => {
                const seleccionado = jugadorSaliente?.slot === slot;
                return (
                  <div
                    key={slot}
                    onClick={() => setJugadorSaliente(seleccionado ? null : { slot, jugador })}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '6px 10px',
                      background: seleccionado ? '#ff444422' : '#111827',
                      border: `1px solid ${seleccionado ? '#ff4444' : '#1e293b'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: '0.15s',
                    }}
                    onMouseEnter={e => { if (!seleccionado) e.currentTarget.style.borderColor = '#ff444488'; }}
                    onMouseLeave={e => { if (!seleccionado) e.currentTarget.style.borderColor = '#1e293b'; }}
                  >
                    {/* Indicador de estado */}
                    {(() => {
                      const st = jugador.status;
                      const col = st === 'titular' ? '#22c55e' : st === 'duda' ? '#f97316' : st === 'lesionado' ? '#ef4444' : '#64748b';
                      const label = st === 'titular' ? '●' : st === 'duda' ? '?' : st === 'lesionado' ? '✕' : '–';
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', flexShrink: 0, width: '22px' }}>
                          <span style={{ color: col, fontSize: '0.9rem', lineHeight: 1, filter: `drop-shadow(0 0 4px ${col})` }}>{label}</span>
                          <span style={{ color: col, fontSize: '0.5rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {st === 'titular' ? 'TIT' : st === 'duda' ? 'DUD' : st === 'lesionado' ? 'LES' : 'NJ'}
                          </span>
                        </div>
                      );
                    })()}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ color: seleccionado ? '#ff6666' : '#e2e8f0', fontWeight: 'bold', fontSize: '0.82rem', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {jugador.apellido ? `${jugador.apellido}, ${jugador.nombre}` : jugador.nombre}
                      </p>
                      <p style={{ color: '#64748b', fontSize: '0.68rem', margin: '1px 0 0' }}>
                        {jugador.club?.sigla} · ${(jugador.precio / 1_000_000).toFixed(1)}M
                      </p>
                    </div>
                    {seleccionado && (
                      <span style={{ color: '#ff4444', fontSize: '0.9rem', flexShrink: 0 }}>✓</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Flecha — oculta en mobile */}
          {window.innerWidth > 768 && (
            <div style={{ textAlign: 'center', color: jugadorSaliente ? '#a855f7' : '#334155', fontSize: '1.6rem', paddingTop: '60px', transition: '0.3s' }}>
              →
            </div>
          )}

          {/* ── Derecha: mercado para elegir quién entra ── */}
          <div style={{
            background: '#0a0f19',
            border: `1px solid ${jugadorSaliente ? '#a855f766' : '#334155'}`,
            borderRadius: '12px',
            padding: '14px',
            opacity: jugadorSaliente ? 1 : 0.4,
            transition: '0.2s',
            pointerEvents: jugadorSaliente ? 'all' : 'none',
          }}>
            <p style={{ color: '#a855f7', fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 10px', textAlign: 'center' }}>
              ¿QUIÉN ENTRA? ({posNecesaria})
            </p>
            {!jugadorSaliente ? (
              <p style={{ color: '#475569', fontSize: '0.8rem', textAlign: 'center', padding: '20px 0' }}>
                Primero elegí quién sale →
              </p>
            ) : (
              <MercadoMini
                posicion={posNecesaria}
                equipoActual={equipoTemporal}
                disponible={disponible}
                onSeleccionar={(jugadorEntra) => {
                  onResolverConflicto({
                    slotSale: jugadorSaliente.slot,
                    jugadorSale: jugadorSaliente.jugador,
                    jugadorEntra,
                  });
                }}
              />
            )}
          </div>
        </div>

        {/* Cancelar */}
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            onClick={onCancelar}
            style={{
              background: 'none', border: '1px solid #334155',
              borderRadius: '8px', color: '#64748b',
              padding: '8px 24px', cursor: 'pointer', fontSize: '0.82rem',
              transition: '0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#ff4444'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#334155'}
          >
            Cancelar — mantener formación actual
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Mercado mini embebido en el modal ────────────────────────────────────────
const MercadoMini = ({ posicion, equipoActual, disponible, onSeleccionar }) => {
  const [jugadores, setJugadores]   = useState([]);
  const [busqueda, setBusqueda]     = useState('');
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    const traer = async () => {
      setLoading(true);
      const { data: posData } = await supabase
        .from('posiciones').select('id').eq('sigla', posicion).single();
      if (!posData) { setLoading(false); return; }

      const { data } = await supabase
        .from('jugadores')
        .select('id, nombre, apellido, precio, pie_habil, nacionalidad, status, dorsal, contexto, seleccion, liga_club, posicion:posiciones(sigla), club:clubes(sigla)')
        .eq('posicion_id', posData.id)
        .order('apellido');
      setJugadores(data || []);
      setLoading(false);
    };
    traer();
  }, [posicion]);

  const filtrados = jugadores.filter(j =>
    norm(`${j.nombre} ${j.apellido}`).includes(norm(busqueda))
  );

  const idsEnEquipo = new Set(Object.values(equipoActual || {}).filter(Boolean).map(j => j.id));

  return (
    <div>
      <input
        placeholder={`Buscar ${posicion}...`}
        value={busqueda}
        onChange={e => setBusqueda(e.target.value)}
        style={{
          width: '100%', padding: '7px 10px', marginBottom: '8px',
          background: '#111827', border: '1px solid #1e293b',
          borderRadius: '6px', color: '#fff', fontSize: '0.82rem',
          boxSizing: 'border-box',
        }}
      />
      <div style={{ maxHeight: '220px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {loading ? (
          <p style={{ color: '#475569', fontSize: '0.8rem', textAlign: 'center' }}>Cargando...</p>
        ) : filtrados.length === 0 ? (
          <p style={{ color: '#475569', fontSize: '0.8rem', textAlign: 'center' }}>Sin resultados</p>
        ) : filtrados.map(j => {
          const yaEsta    = idsEnEquipo.has(j.id);
          const sinPlata  = (j.precio || 0) > disponible;
          const deshabilitado = yaEsta || sinPlata;

          return (
            <div
              key={j.id}
              onClick={() => !deshabilitado && onSeleccionar(j)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 10px',
                background: deshabilitado ? '#0a0f19' : '#111827',
                borderRadius: '6px',
                cursor: deshabilitado ? 'not-allowed' : 'pointer',
                opacity: deshabilitado ? 0.45 : 1,
                border: '1px solid transparent',
                transition: '0.15s',
              }}
              onMouseEnter={e => { if (!deshabilitado) e.currentTarget.style.borderColor = '#a855f7'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                {/* Status dot */}
                {(() => {
                  const st = j.status;
                  const col = st === 'titular' ? '#22c55e' : st === 'duda' ? '#f97316' : st === 'lesionado' ? '#ef4444' : '#64748b';
                  return <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: col, flexShrink: 0, boxShadow: `0 0 5px ${col}88` }} />;
                })()}
                <div style={{ minWidth: 0 }}>
                  <p style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: '600', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {j.apellido ? `${j.apellido}, ${j.nombre}` : j.nombre}
                  </p>
                  <p style={{ color: '#64748b', fontSize: '0.7rem', margin: '2px 0 0' }}>
                    {j.club?.sigla} · {j.pie_habil === 'Izquierdo' ? 'IZQ' : 'DER'}
                    {yaEsta && <span style={{ color: '#f59e0b', marginLeft: '6px' }}>Ya en equipo</span>}
                    {sinPlata && !yaEsta && <span style={{ color: '#ff4444', marginLeft: '6px' }}>Sin presupuesto</span>}
                  </p>
                </div>
              </div>
              <span style={{ color: '#a855f7', fontWeight: 'bold', fontSize: '0.82rem', flexShrink: 0, marginLeft: '8px' }}>
                ${(j.precio / 1_000_000).toFixed(1)}M
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Componente principal ─────────────────────────────────────────────────────

const PROXIMAMENTE = true; // ← cambiar a false cuando esté listo

const EquipoLibre = ({
  session,
  equipoLibre, setEquipoLibre,
  formacionLibre, setFormacionLibre,
  mercadoAbierto, setMercadoAbierto,
  posicionSeleccionada, setPosicionSeleccionada,
  indexAEditar, setIndexAEditar,
  slotSeleccionado, setSlotSeleccionado,
  menuContextual, setMenuContextual,
  jornadaActiva = 1,
  onCopiarATactico,
}) => {
  const posLibre = COORDENADAS_FORMACIONES[formacionLibre] || COORDENADAS_FORMACIONES['4-3-3'];
  const gastado  = Object.values(equipoLibre).reduce((acc, j) => acc + (j?.precio || 0), 0);
  const disponible = PRESUPUESTO_LIBRE - gastado;

  // Estado del asistente de cambio de formación
  const [modalCambio, setModalCambio]         = useState(null);
  const [capitanIndex, setCapitanIndex]       = useState(null);
  const [confirmado, setConfirmado]           = useState(false);
  const [confirmando, setConfirmando]         = useState(false);
  const [ventana, setVentana]                 = useState(null);
  const [jornadasLibre, setJornadasLibre]     = useState([]);
  const [jornadaViendo, setJornadaViendo]     = useState(jornadaActiva);
  const [equipoHistorico, setEquipoHistorico] = useState(null);
  const [toast, setToast]                     = useState(null);
  const [puntajeJugador, setPuntajeJugador]   = useState({}); // { jugador_id: pts }
  const [mobileTab, setMobileTab]             = useState('suplentes'); // mobile panel tab

  const mostrarToast = (msg, tipo = 'ok') => {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 3000);
  };
  // { formacionNueva, conflictos, conflictoActual, equipoTemporal }

  const cerrarTodo = () => { setMercadoAbierto(false); setSlotSeleccionado(null); setMenuContextual(null); };

  // ── Cargar equipo libre desde DB al montar ──────────────────────────────
  useEffect(() => {
    if (!session?.user?.id) return;
    const cargar = async () => {
      const [{ data: ventanas }, { data: todasJornadas }] = await Promise.all([
        supabase.from('ventana_transferencias').select('*').eq('activa', true).order('numero_fecha', { ascending: false }).limit(1),
        supabase.from('drafts').select('jornada, confirmado').eq('user_id', session.user.id).gte('jornada', 0).order('jornada'),
      ]);
      setVentana(ventanas?.[0] || null);
      const jornadaActualVentana = ventanas?.[0]?.numero_fecha ?? null;

      // Deduplicar: si hay jornada 0, no mostrar jornada 1 (ambas son "Fecha 1")
      const tieneJ0 = (todasJornadas || []).some(j => j.jornada === 0);
      let jornadasFiltradas = tieneJ0
        ? (todasJornadas || []).filter(j => j.jornada !== 1)
        : (todasJornadas || []);
      // Siempre incluir la jornada activa
      const tieneActiva = jornadasFiltradas.some(j => j.jornada === jornadaActualVentana);
      if (!tieneActiva && jornadaActualVentana) {
        jornadasFiltradas = [...jornadasFiltradas, { jornada: jornadaActualVentana, confirmado: false }];
      }
      setJornadasLibre(jornadasFiltradas);

      // Buscar primero en jornada activa, fallback a jornada 0 (legacy)
      let data = null;
      if (jornadaActualVentana) {
        const { data: dataActiva } = await supabase.from('drafts')
          .select('equipo_libre, formacion_libre, capitan_index_libre, confirmado, jornada_confirmada_libre')
          .eq('user_id', session.user.id).eq('jornada', jornadaActualVentana).maybeSingle();
        data = dataActiva;
      }
      if (!data || !data.equipo_libre || Object.keys(data.equipo_libre).length === 0) {
        const { data: dataLegacy } = await supabase.from('drafts')
          .select('equipo_libre, formacion_libre, capitan_index_libre, confirmado, jornada_confirmada_libre')
          .eq('user_id', session.user.id).eq('jornada', 0).maybeSingle();
        if (dataLegacy?.equipo_libre && Object.keys(dataLegacy.equipo_libre).length > 0) data = dataLegacy;
      }

      if (data?.equipo_libre && Object.keys(data.equipo_libre).length > 0) {
        setEquipoLibre(data.equipo_libre);
      }
      if (data?.formacion_libre) setFormacionLibre(data.formacion_libre);
      if (data?.capitan_index_libre != null) setCapitanIndex(data.capitan_index_libre);
      const confirmadoParaEstaJornada = data?.confirmado && data?.jornada_confirmada_libre === jornadaActualVentana;
      setConfirmado(confirmadoParaEstaJornada || false);

      // Puntos: solo mostrar si ya se distribuyeron (existe en torneo_puntos para jornada libre)
      // Si el draft activo no tiene jornada_confirmada_libre, buscar en jornada 0 (legacy)
      let jornadaConfirmadaLibre = data?.jornada_confirmada_libre;
      if (!jornadaConfirmadaLibre) {
        const { data: d0 } = await supabase.from('drafts').select('jornada_confirmada_libre')
          .eq('user_id', session.user.id).eq('jornada', 0).maybeSingle();
        jornadaConfirmadaLibre = d0?.jornada_confirmada_libre;
      }
      if (jornadaConfirmadaLibre && jornadaConfirmadaLibre > 0) {
        const jornadaDistribuida = jornadaConfirmadaLibre;
        // Buscar torneos libres del usuario
        const { data: partic } = await supabase.from('torneo_participantes')
          .select('torneo_id, torneo:torneos(tipo)').eq('user_id', session.user.id);
        const tIds = (partic || []).filter(p => p.torneo?.tipo === 'libre').map(p => p.torneo_id);
        if (tIds.length > 0) {
          const { data: fechas } = await supabase.from('torneo_fechas')
            .select('id').in('torneo_id', tIds).eq('numero_fecha', jornadaDistribuida);
          const fIds = (fechas || []).map(f => f.id);
          if (fIds.length > 0) {
            const { data: rows } = await supabase.from('torneo_puntos')
              .select('puntos_por_jugador').in('fecha_id', fIds).eq('user_id', session.user.id);
            const best = (rows || []).find(r => r.puntos_por_jugador && Object.keys(r.puntos_por_jugador).length > 0);
            setPuntajeJugador(best?.puntos_por_jugador || {});
          } else { setPuntajeJugador({}); }
        } else { setPuntajeJugador({}); }
      } else {
        setPuntajeJugador({});
      }
    };
    cargar();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  // ── Guardar en DB ────────────────────────────────────────────────────────
  const guardarEnDB = async (nuevoEquipo, nuevaFormacion) => {
    if (!session?.user?.id || equipoHistorico || confirmado || !ventana) return;
    await supabase.from('drafts').upsert({
      user_id: session.user.id,
      jornada: jornadaActiva,
      equipo_libre: nuevoEquipo,
      formacion_libre: nuevaFormacion,
    }, { onConflict: 'user_id,jornada' });
  };

  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);

  const pedirConfirmacion = () => {
    if (confirmando) return;
    const titulares = Object.entries(equipoLibre).filter(([k]) => Number(k) <= 10).map(([,v]) => v).filter(Boolean);
    const suplentes = Object.entries(equipoLibre).filter(([k]) => Number(k) >= 20).map(([,v]) => v).filter(Boolean);
    if (titulares.length < 11) { mostrarToast(`Faltan ${11 - titulares.length} titulares`, 'error'); return; }
    if (suplentes.length < 4)  { mostrarToast(`Faltan ${4 - suplentes.length} suplentes`, 'error'); return; }
    if (capitanIndex === null || capitanIndex === undefined) { mostrarToast('Elegí un capitán antes de confirmar', 'error'); return; }
    setMostrarConfirmar(true);
  };

  const confirmarEquipo = async () => {
    if (confirmando) return;

    const titulares = Object.entries(equipoLibre).filter(([k]) => Number(k) <= 10).map(([,v]) => v).filter(Boolean);
    const suplentes = Object.entries(equipoLibre).filter(([k]) => Number(k) >= 20).map(([,v]) => v).filter(Boolean);

    if (titulares.length < 11) {
      mostrarToast(`Faltan ${11 - titulares.length} titular${11 - titulares.length > 1 ? 'es' : ''} (necesitás 11 titulares)`, 'error');
      return;
    }
    if (suplentes.length < 4) {
      mostrarToast(`Faltan ${4 - suplentes.length} suplente${4 - suplentes.length > 1 ? 's' : ''} (necesitás 4 suplentes)`, 'error');
      return;
    }
    if (capitanIndex === null || capitanIndex === undefined) {
      mostrarToast('Tenés que elegir un capitán antes de confirmar', 'error');
      return;
    }

    setConfirmando(true);
    await supabase.from('drafts').upsert({
      user_id: session.user.id,
      jornada: jornadaActiva,
      equipo_libre: equipoLibre,
      formacion_libre: formacionLibre,
      capitan_index_libre: capitanIndex,
      confirmado: true,
      confirmado_en: new Date().toISOString(),
      jornada_confirmada_libre: jornadaActiva, // ⚠️ para saber a qué jornada corresponde este confirmado
    }, { onConflict: 'user_id,jornada' });
    setConfirmado(true);
    setConfirmando(false);
  };

  // ── Cambio de formación inteligente ─────────────────────────────────────
  const handleCambiarFormacion = async (nuevaFormacion) => {
    if (nuevaFormacion === formacionLibre) return;
    if (confirmado || !ventana) return;

    // Verificar si hay titulares cargados
    const titularesActuales = Object.entries(equipoLibre)
      .filter(([k, v]) => Number(k) <= 10 && v);

    if (titularesActuales.length === 0) {
      // Sin jugadores → cambio directo sin modal
      setFormacionLibre(nuevaFormacion);
      await guardarEnDB({}, nuevaFormacion);
      return;
    }

    // Calcular conflictos
    const conflictos = calcularConflictos(equipoLibre, formacionLibre, nuevaFormacion);

    if (conflictos.length === 0) {
      // Sin conflictos: reubicar jugadores en los nuevos slots
      const nuevoEquipo = reubicarJugadores(equipoLibre, formacionLibre, nuevaFormacion);
      setFormacionLibre(nuevaFormacion);
      setEquipoLibre(nuevoEquipo);
      await guardarEnDB(nuevoEquipo, nuevaFormacion);
      return;
    }

    // Hay conflictos → abrir asistente
    // Preparar equipo temporal: reubicar los jugadores que SÍ encajan
    const equipoTemporal = reubicarJugadores(equipoLibre, formacionLibre, nuevaFormacion, conflictos);
    setModalCambio({
      formacionNueva: nuevaFormacion,
      conflictos,
      conflictoActual: 0,
      equipoTemporal,
    });
  };

  /**
   * Reubica los jugadores del equipo viejo en los slots de la nueva formación.
   * Excluye los jugadores que son candidatos a conflicto (todos los de posVieja excedente).
   * Así el equipoTemporal que se pasa al modal ya no tiene esos jugadores.
   */
  const reubicarJugadores = (equipoViejo, formVieja, formNueva, conflictosAExcluir = []) => {
    const posVieja = COORDENADAS_FORMACIONES[formVieja] || [];
    const posNueva = COORDENADAS_FORMACIONES[formNueva] || [];

    // Contar cuántos jugadores de cada posición hay que excluir (= cantidad de excedentes)
    // NO excluimos todos los candidatos, solo tantos como conflictos hay de esa posición
    const excluirPorPos = {};
    conflictosAExcluir.forEach(c => {
      excluirPorPos[c.posVieja] = (excluirPorPos[c.posVieja] || 0) + 1;
    });

    // Para cada posición excedente, marcar los ÚLTIMOS N jugadores de esa pos para excluir
    // (el usuario elegirá entre ellos — los que no elija se reubicarán normalmente)
    const idsExcluidos = new Set();
    Object.entries(excluirPorPos).forEach(([pos, cantAExcluir]) => {
      const jugadoresDeEsaPos = Object.entries(equipoViejo)
        .filter(([k, v]) => Number(k) <= 10 && v && posVieja[Number(k)]?.pos === pos)
        .map(([, v]) => v);
      // Excluir los últimos N (quedan los primeros que sí encajan)
      jugadoresDeEsaPos.slice(-cantAExcluir).forEach(j => idsExcluidos.add(j.id));
    });

    // Slots disponibles en la nueva formación por posición
    const slotsPorPosNueva = {};
    posNueva.forEach((p, i) => {
      if (!slotsPorPosNueva[p.pos]) slotsPorPosNueva[p.pos] = [];
      slotsPorPosNueva[p.pos].push(i);
    });

    const nuevoEquipo = {};
    const usados = new Set();

    // Mantener suplentes intactos
    [20, 21, 22, 23].forEach(id => {
      if (equipoViejo[id]) nuevoEquipo[id] = equipoViejo[id];
    });

    // Reubicar titulares que NO están excluidos
    Object.entries(equipoViejo)
      .filter(([k, v]) => Number(k) <= 10 && v && !idsExcluidos.has(v.id))
      .forEach(([k, jugador]) => {
        const posJ = posVieja[Number(k)]?.pos;
        if (!posJ) return;
        const disponibles = (slotsPorPosNueva[posJ] || []).filter(s => !usados.has(s));
        if (disponibles.length > 0) {
          nuevoEquipo[disponibles[0]] = jugador;
          usados.add(disponibles[0]);
        }
      });

    return nuevoEquipo;
  };

  const handleResolverConflicto = async ({ slotSale, jugadorSale, jugadorEntra }) => {
    const { formacionNueva, conflictos, conflictoActual, equipoTemporal } = modalCambio;
    const conflicto = conflictos[conflictoActual];

    // Marcar el slot original elegido para excluirlo en conflictos posteriores
    const conflictosActualizados = conflictos.map((c, i) =>
      i === conflictoActual ? { ...c, _slotElegido: slotSale } : c
    );

    // El equipoTemporal ya NO tiene los candidatos (fueron excluidos en reubicarJugadores).
    // Solo necesitamos agregar el nuevo jugador que entra.
    const posNueva = COORDENADAS_FORMACIONES[formacionNueva] || [];
    const slotDestino = posNueva
      .map((p, i) => ({ pos: p.pos, slot: i }))
      .find(({ pos, slot }) => pos === conflicto.posNecesaria && equipoTemporal[slot] === undefined)
      ?.slot;

    if (slotDestino === undefined) return;

    const nuevoEquipoTemporal = { ...equipoTemporal, [slotDestino]: jugadorEntra };
    const siguiente = conflictoActual + 1;

    if (siguiente >= conflictos.length) {
      // Si hay jugador pendiente de insertar (vino de modoLibre con pos distinta), agregarlo
      let equipoFinal = nuevoEquipoTemporal;
      if (modalCambio.jugadorPendiente) {
        const posNueva2 = COORDENADAS_FORMACIONES[formacionNueva] || [];
        const posJ = modalCambio.jugadorPendiente.posicion?.sigla || modalCambio.jugadorPendiente.posicion;
        const slotPendiente = posNueva2.findIndex((p, i) => p.pos === posJ && !equipoFinal[i]);
        if (slotPendiente >= 0) equipoFinal = { ...equipoFinal, [slotPendiente]: modalCambio.jugadorPendiente };
      }
      setFormacionLibre(formacionNueva);
      setEquipoLibre(equipoFinal);
      setModalCambio(null);
      await guardarEnDB(equipoFinal, formacionNueva);
    } else {
      setModalCambio(prev => ({
        ...prev,
        conflictos: conflictosActualizados,
        conflictoActual: siguiente,
        equipoTemporal: nuevoEquipoTemporal,
      }));
    }
  };

  // ── Cancelar el asistente → no cambia nada ───────────────────────────────
  const handleCancelarCambio = () => {
    setModalCambio(null);
  };

  // ── Handlers normales de slots ────────────────────────────────────────────
  const handleCapitan = async (idx) => {
    if (equipoHistorico || confirmado || !ventana) { setMenuContextual(null); return; }
    const nuevo = capitanIndex === idx ? null : idx;
    setCapitanIndex(nuevo);
    setMenuContextual(null);
    setSlotSeleccionado(null);
    await supabase.from('drafts').upsert({ user_id: session.user.id, jornada: jornadaActiva, capitan_index_libre: nuevo }, { onConflict: 'user_id,jornada' });
  };

  const handleClickSlot = (e, i, pos) => {
    if (equipoHistorico) return;
    if (confirmado || !ventana) return; // ⚠️ equipo confirmado o ventana cerrada — solo lectura
    if (slotSeleccionado === i) { cerrarTodo(); return; }
    if (mercadoAbierto) { cerrarTodo(); return; }
    if (!equipoLibre[i]) {
      setPosicionSeleccionada(pos);
      setIndexAEditar(i);
      setMercadoAbierto(true);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setSlotSeleccionado(i);
      setMenuContextual({ index: i, pos, esSuplente: false, x: rect.left + rect.width / 2, y: rect.top });
    }
  };

  const handleClickSuplente = (e, slot) => {
    if (equipoHistorico) return;
    if (confirmado || !ventana) return; // ⚠️ equipo confirmado o ventana cerrada
    if (slotSeleccionado === slot.id) { cerrarTodo(); return; }
    if (mercadoAbierto) { cerrarTodo(); return; }
    if (!equipoLibre[slot.id]) {
      setPosicionSeleccionada(slot.pos);
      setIndexAEditar(slot.id);
      setMercadoAbierto(true);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setSlotSeleccionado(slot.id);
      setMenuContextual({ index: slot.id, pos: slot.pos, esSuplente: true, x: rect.right + 8, y: rect.top });
    }
  };

  const handleIntercambiarLibre = (idxA, idxB) => {
    setEquipoLibre(prev => ({ ...prev, [idxA]: prev[idxB], [idxB]: prev[idxA] }));
    const nuevo = { ...equipoLibre, [idxA]: equipoLibre[idxB], [idxB]: equipoLibre[idxA] };
    guardarEnDB(nuevo, formacionLibre);
    setMenuContextual(null);
    setSlotSeleccionado(null);
  };

  const handleQuitarLibre = (idx) => {
    const nuevo = { ...equipoLibre };
    delete nuevo[idx];
    setEquipoLibre(nuevo);
    guardarEnDB(nuevo, formacionLibre);
    setMenuContextual(null);
    setSlotSeleccionado(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (PROXIMAMENTE) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', gap:'16px' }}>
      <h2 style={{ color:'#a855f7', fontWeight:'bold', fontSize:'1.4rem', margin:0 }}>Proximamente</h2>
      <p style={{ color:'#475569', fontSize:'0.9rem', margin:0 }}>El Equipo Libre estará disponible pronto.</p>
    </div>
  );

  return (
    <>
    <div className="tactical-screen-container">

      {/* Modal asistente de cambio de formación */}
      {modalCambio && (
        <ModalCambioFormacion
          conflictos={modalCambio.conflictos}
          conflictoActual={modalCambio.conflictoActual}
          equipoTemporal={modalCambio.equipoTemporal}
          equipoOriginal={equipoLibre}
          disponible={disponible}
          onResolverConflicto={handleResolverConflicto}
          onCancelar={handleCancelarCambio}
        />
      )}

      {/* Mercado lateral normal */}
      {mercadoAbierto && (
        <Mercado
          posicionInicial={posicionSeleccionada}
          modoLibre={true}
          onClose={cerrarTodo}
          equipoActual={equipoLibre}
          onSeleccionarJugador={async (jugador) => {
            const precioActual = equipoLibre[indexAEditar]?.precio || 0;
            const costo = (jugador.precio || 0) - precioActual;
            if (disponible - costo < 0) {
              mostrarToast(`¡Presupuesto insuficiente! Te faltan $${(costo - disponible).toLocaleString()}`, 'error');
              return;
            }
            const posSlot = posLibre[indexAEditar]?.pos;
            const posJugador = jugador.posicion?.sigla || jugador.posicion;

            // Si la posición no coincide → abrir asistente de cambio de formación
            if (posSlot && posJugador && posSlot !== posJugador) {
              cerrarTodo();
              // Construir un "conflicto manual": el slot actual necesita posJugador
              // en vez de posSlot — hay que cambiar la formación
              const posVieja = posLibre;
              // Buscar formaciones que tengan un slot libre de posJugador
              const formacionesCompatibles = FORMACIONES.filter(f => {
                const pos = COORDENADAS_FORMACIONES[f] || [];
                const countNuevo = pos.filter(p => p.pos === posJugador).length;
                const countViejo = Object.entries(equipoLibre).filter(([k,v]) => Number(k) <= 10 && v && posVieja[Number(k)]?.pos === posJugador).length;
                return countNuevo > countViejo;
              });
              if (formacionesCompatibles.length === 0) {
                mostrarToast(`No hay formación disponible para poner un ${posJugador} ahí`, 'error');
                return;
              }
              // Usar la primera formación compatible
              const nuevaFormacion = formacionesCompatibles[0];
              const conflictos = calcularConflictos(equipoLibre, formacionLibre, nuevaFormacion);
              const equipoTemporal = reubicarJugadores(equipoLibre, formacionLibre, nuevaFormacion, conflictos);
              // Si no hay conflictos, cambiar directamente y poner el jugador
              if (conflictos.length === 0) {
                const posNueva = COORDENADAS_FORMACIONES[nuevaFormacion] || [];
                const slotDestino = posNueva.findIndex((p, i) => p.pos === posJugador && !equipoTemporal[i]);
                if (slotDestino >= 0) {
                  const nuevoEquipo = { ...equipoTemporal, [slotDestino]: jugador };
                  setFormacionLibre(nuevaFormacion);
                  setEquipoLibre(nuevoEquipo);
                  await guardarEnDB(nuevoEquipo, nuevaFormacion);
                }
                return;
              }
              setModalCambio({ formacionNueva: nuevaFormacion, conflictos, conflictoActual: 0, equipoTemporal, jugadorPendiente: jugador });
              return;
            }

            // Posición coincide — poner normalmente
            const nuevoEquipo = { ...equipoLibre, [indexAEditar]: jugador };
            setEquipoLibre(nuevoEquipo);
            cerrarTodo();
            await guardarEnDB(nuevoEquipo, formacionLibre);
          }}
        />
      )}

      <div className="main-tactical-layout">
        {/* SUPLENTES */}
        <aside className="left-panel-compact">
          <h3 className="panel-title-compact">Suplentes</h3>
          {CONFIG_SUPLENTES.map(slot => (
            <div
              key={slot.id}
              className={`subs-slot-compact
                ${slotSeleccionado !== null && slotSeleccionado !== slot.id ? 'slot-dimmed' : ''}
                ${slotSeleccionado === slot.id ? 'slot-selected' : ''}
              `}
              onClick={(e) => handleClickSuplente(e, slot)}
            >
              <CardJugador jugador={equipoLibre[slot.id]} posicion={slot.pos} nombre={slot.label} isMini puntos={puntajeJugador[equipoLibre[slot.id]?.id] ?? null} />
            </div>
          ))}
        </aside>

        {/* CANCHA */}
        <CanchaGrid
          posiciones={posLibre}
          equipo={equipoHistorico ? (equipoHistorico.equipo_libre || {}) : equipoLibre}
          slotSeleccionado={slotSeleccionado}
          onClickSlot={handleClickSlot}
          capitanIndex={capitanIndex}
          onCapitan={handleCapitan}
          puntajeJugador={puntajeJugador}
          infoBar={
            <>
              <span>FORMACIÓN {equipoHistorico ? equipoHistorico.formacion_libre : formacionLibre}</span>
              <span className="separator">|</span>
              {Object.keys(puntajeJugador).length > 0 && (() => {
                const totalPts = Object.values(puntajeJugador).reduce((a, b) => a + (b || 0), 0);
                return totalPts > 0 ? (
                  <>
                    <span style={{ color: '#a855f7', fontWeight: 'bold' }}>FECHA {jornadaViendo}: {totalPts} pts</span>
                    <span className="separator">|</span>
                  </>
                ) : null;
              })()}
              {!equipoHistorico && !confirmado && ventana && (
                <select
                  value={formacionLibre}
                  onChange={(e) => handleCambiarFormacion(e.target.value)}
                  style={{ background: '#0a0f19', color: '#a855f7', border: '1px solid #a855f7', borderRadius: '4px', padding: '2px 6px', fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  {FORMACIONES.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              )}
              {jornadasLibre.filter(j => j.jornada === 0 || j.jornada <= jornadaActiva).length > 1 && (
                <>
                  <span className="separator">|</span>
                  {jornadasLibre.filter(j => j.jornada === 0 || j.jornada <= jornadaActiva).map(j => (
                    <button key={j.jornada} onClick={async () => {
                      setJornadaViendo(j.jornada);
                      if (j.jornada === jornadaActiva) {
                        setEquipoHistorico(null);
                        setPuntajeJugador({});
                        // Recargar confirmado real desde DB — comparar con jornada activa
                        const { data: draftActual } = await supabase.from('drafts')
                          .select('confirmado, jornada_confirmada_libre').eq('user_id', session.user.id).eq('jornada', jornadaActiva).maybeSingle();
                        setConfirmado(draftActual?.confirmado && draftActual?.jornada_confirmada_libre === jornadaActiva ? true : false);
                      } else {
                        let { data } = await supabase.from('drafts').select('*').eq('user_id', session.user.id).eq('jornada', j.jornada).maybeSingle();
                        // Si no tiene equipo_libre en esa jornada, buscar en jornada 0 (legacy)
                        if (!data?.equipo_libre || Object.keys(data.equipo_libre).length === 0) {
                          const { data: d0 } = await supabase.from('drafts').select('*').eq('user_id', session.user.id).eq('jornada', 0).maybeSingle();
                          if (d0?.equipo_libre) data = { ...data, ...d0, jornada: j.jornada };
                        }
                        setEquipoHistorico(data);
                        // Cargar puntos libres históricos
                        try {
                          const { data: partic2 } = await supabase.from('torneo_participantes')
                            .select('torneo_id, torneo:torneos(tipo)').eq('user_id', session.user.id);
                          const tIds = (partic2 || []).filter(p => p.torneo?.tipo === 'libre').map(p => p.torneo_id);
                          if (tIds.length) {
                            // jornada 0 = legacy fecha 1
                            const numeroFechaH = j.jornada === 0 ? 1 : j.jornada;
                            const { data: fechasH } = await supabase.from('torneo_fechas')
                              .select('id').in('torneo_id', tIds).eq('numero_fecha', numeroFechaH);
                            const fIds = (fechasH || []).map(f => f.id);
                            if (fIds.length) {
                              const { data: rowsH } = await supabase.from('torneo_puntos')
                                .select('puntos_por_jugador, puntos').in('fecha_id', fIds).eq('user_id', session.user.id);
                              const best = (rowsH || []).sort((a,b) => (b.puntos||0)-(a.puntos||0))[0];
                              setPuntajeJugador(best?.puntos_por_jugador && Object.keys(best.puntos_por_jugador).length > 0 ? best.puntos_por_jugador : {});
                            } else setPuntajeJugador({});
                          }
                        } catch(e) { setPuntajeJugador({}); }
                      }
                    }}
                      style={{ padding:'2px 8px', borderRadius:'4px', fontSize:'0.72rem', fontWeight:'bold', cursor:'pointer', border:`1px solid ${jornadaViendo === j.jornada ? '#a855f7' : '#334155'}`, background: jornadaViendo === j.jornada ? '#a855f722' : 'transparent', color: jornadaViendo === j.jornada ? '#a855f7' : '#475569' }}>
                      Fecha {j.jornada === 0 ? 1 : j.jornada}{j.confirmado ? '' : ''}
                    </button>
                  ))}
                  {jornadaViendo !== jornadaActiva && (
                    <span style={{ color:'#f59e0b', fontSize:'0.68rem', fontStyle:'italic' }}>solo lectura</span>
                  )}
                </>
              )}
            </>
          }
        />

        {/* PANEL DERECHO */}
        <aside className="restrictions-panel-compact">
          <h3 className="king-title">PRESUPUESTO</h3>
          <div style={{ padding: '12px', background: '#0a0f19', borderRadius: '8px', marginBottom: '8px', border: `1px solid ${disponible < 0 ? '#ff4444' : '#333'}` }}>
            <p style={{ color: disponible < 0 ? '#ff4444' : '#22c55e', fontWeight: 'bold', fontSize: '1.1rem', margin: 0, textAlign: 'center' }}>
              ${disponible.toLocaleString()}
            </p>
          </div>

          {/* Puntaje total */}
          {Object.keys(puntajeJugador).length > 0 && (() => {
            const totalPts = Object.values(puntajeJugador).reduce((a, b) => a + b, 0);
            return (
              <div style={{ padding: '10px 12px', background: '#0a0f19', borderRadius: '8px', marginBottom: '8px', border: '1px solid #a855f733', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Puntos
                </span>
                <span style={{ color: '#a855f7', fontWeight: 'bold', fontSize: '1.2rem', textShadow: '0 0 10px rgba(168,85,247,0.4)' }}>
                  {totalPts}
                </span>
              </div>
            );
          })()}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: '#0a1828', borderRadius: '6px' }}>
              <span style={{ color: '#64748b', fontSize: '0.8rem' }}>Total</span>
              <span style={{ color: '#a855f7', fontWeight: 'bold', fontSize: '0.85rem' }}>${PRESUPUESTO_LIBRE.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: '#0a1828', borderRadius: '6px' }}>
              <span style={{ color: '#64748b', fontSize: '0.8rem' }}>Gastado</span>
              <span style={{ color: gastado > PRESUPUESTO_LIBRE ? '#ff4444' : '#94a3b8', fontWeight: 'bold', fontSize: '0.85rem' }}>${gastado.toLocaleString()}</span>
            </div>
          </div>
          {/* Botón confirmar */}
          {ventana && !equipoHistorico && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
              {confirmado ? (
                <>
                  <div style={{ padding: '10px', borderRadius: '8px', border: '1px solid #22c55e44', background: '#22c55e15', textAlign: 'center' }}>
                    <p style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '0.85rem', margin: 0 }}>✓ Equipo Confirmado</p>
                  </div>
                  {(() => { const ahora = new Date(); const abierta = !ventana || (ahora >= new Date(ventana.apertura) && ahora <= new Date(ventana.cierre)); return abierta ? (
                    <button onClick={async () => {
                      await supabase.from('drafts').update({ confirmado: false }).eq('user_id', session.user.id).eq('jornada', jornadaActiva);
                      setConfirmado(false);
                      mostrarToast('Podés editar tu equipo hasta el cierre de la ventana', 'ok');
                    }} style={{ width: '100%', background: 'transparent', color: '#f59e0b', border: '1px solid #f59e0b55', borderRadius: '8px', padding: '8px', fontWeight: 'bold', fontSize: '0.8rem', cursor: 'pointer' }}>
                      ✏️ Editar equipo
                    </button>
                  ) : null; })()}
                </>
              ) : (
                <button onClick={pedirConfirmacion} disabled={confirmando}
                  style={{ width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #a855f7', background:'#a855f715', color:'#a855f7', fontWeight:'bold', fontSize:'0.85rem', cursor:'pointer', transition:'0.2s' }}>
                  {confirmando ? 'Confirmando...' : 'Confirmar Equipo'}
                </button>
              )}
            </div>
          )}
          <h3 className="king-title" style={{ color: '#a855f7' }}>MODO LIBRE</h3>
          <p style={{ color: '#64748b', fontSize: '0.78rem', marginTop: '8px', lineHeight: 1.5 }}>
            Sin restricciones.<br />Elegí tu formación y armá el equipo ideal.
          </p>
          {onCopiarATactico && (
            <button onClick={onCopiarATactico} style={{ width: '100%', padding: '7px', marginTop: '12px', background: '#00f2ff15', border: '1px solid #00f2ff44', borderRadius: '7px', color: '#00f2ff', fontSize: '0.78rem', fontWeight: 'bold', cursor: 'pointer', transition: '0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#00f2ff30'}
              onMouseLeave={e => e.currentTarget.style.background = '#00f2ff15'}>
              Copiar 11 de equipo táctico
            </button>
          )}
        </aside>
      </div>

    </div>{/* fin tactical-screen-container */}

      {/* ── PANEL MOBILE: tabs debajo de la cancha ── */}
      <div className="tactico-mobile-panel">
        <div className="tactico-mobile-tabs">
          {[
            { key: 'suplentes', label: 'Suplentes' },
            { key: 'info',      label: 'Info' },
          ].map(({ key, label }) => (
            <button key={key}
              className={`tactico-mobile-tab${mobileTab === key ? ' activo libre' : ''}`}
              onClick={() => setMobileTab(key)}>
              {label}
            </button>
          ))}
          <div className="tactico-mobile-presupuesto">
            <span style={{ color: disponible < 0 ? '#ef4444' : '#22c55e', fontWeight: 'bold', fontSize: '0.72rem' }}>
              ${(disponible / 1000000).toFixed(1)}M
            </span>
          </div>
        </div>

        <div className="tactico-mobile-content">
          {mobileTab === 'suplentes' && (
            <div className="tactico-mobile-suplentes">
              {CONFIG_SUPLENTES_LIBRE.map(slot => {
                const jug = (equipoHistorico ? equipoHistorico.equipo_libre || {} : equipoLibre)[slot.id];
                return (
                  <div key={slot.id} className={`subs-slot-compact${slotSeleccionado === slot.id ? ' slot-selected' : ''}`}
                    onClick={(e) => handleClickSuplente(e, slot)}>
                    <div className="card-cancha-wrap">
                      <CardJugador jugador={jug} posicion={slot.pos} nombre={slot.label} isMini puntos={puntajeJugador[jug?.id] ?? null} />
                      {jug && <span className="card-nombre-ext">{jug.apellido ? `${jug.apellido}${jug.nombre ? ` ${jug.nombre.charAt(0)}.` : ''}` : jug.nombre}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {mobileTab === 'info' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1, padding: '8px 10px', background: '#0a1828', borderRadius: '6px' }}>
                  <p style={{ color: '#64748b', fontSize: '0.7rem', margin: '0 0 2px' }}>Total</p>
                  <p style={{ color: '#a855f7', fontWeight: 'bold', fontSize: '0.9rem', margin: 0 }}>${(PRESUPUESTO_LIBRE / 1000000).toFixed(0)}M</p>
                </div>
                <div style={{ flex: 1, padding: '8px 10px', background: '#0a1828', borderRadius: '6px' }}>
                  <p style={{ color: '#64748b', fontSize: '0.7rem', margin: '0 0 2px' }}>Gastado</p>
                  <p style={{ color: gastado > PRESUPUESTO_LIBRE ? '#ef4444' : '#94a3b8', fontWeight: 'bold', fontSize: '0.9rem', margin: 0 }}>${(gastado / 1000000).toFixed(1)}M</p>
                </div>
              </div>
              {Object.keys(puntajeJugador).length > 0 && (() => {
                const totalPts = Object.values(puntajeJugador).reduce((a, b) => a + b, 0);
                return totalPts > 0 ? (
                  <div style={{ padding: '8px 10px', background: '#0a0f19', borderRadius: '6px', border: '1px solid #a855f733', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#64748b', fontSize: '0.7rem' }}>Puntos fecha</span>
                    <span style={{ color: '#a855f7', fontWeight: 'bold', fontSize: '1.1rem' }}>{totalPts}</span>
                  </div>
                ) : null;
              })()}
              <p style={{ color: '#64748b', fontSize: '0.75rem', margin: 0, lineHeight: 1.5 }}>Sin restricciones. Elegí tu formación con el selector de arriba.</p>
            </div>
          )}
        </div>

        <div className="tactico-mobile-confirmar">
          {equipoHistorico ? (
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
              <p style={{ color: '#64748b', fontWeight: 'bold', fontSize: '0.85rem', margin: 0 }}>Solo lectura</p>
            </div>
          ) : ventana && !confirmado ? (
            <button onClick={pedirConfirmacion} disabled={confirmando} style={{ width: '100%', background: '#a855f7', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px', fontWeight: 'bold', fontSize: '0.95rem', cursor: 'pointer' }}>
              {confirmando ? 'Confirmando...' : '✓ Confirmar Equipo'}
            </button>
          ) : confirmado ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ background: '#22c55e22', border: '1px solid #22c55e44', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                <p style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '0.9rem', margin: 0 }}>✓ Equipo confirmado</p>
              </div>
              {(() => { const ahora = new Date(); const abierta = !ventana || (ahora >= new Date(ventana.apertura) && ahora <= new Date(ventana.cierre)); return abierta ? (
                <button onClick={async () => {
                  await supabase.from('drafts').update({ confirmado: false }).eq('user_id', session.user.id).eq('jornada', jornadaActiva);
                  setConfirmado(false);
                  mostrarToast('Podés editar tu equipo hasta el cierre de la ventana', 'ok');
                }} style={{ width: '100%', background: 'transparent', color: '#f59e0b', border: '1px solid #f59e0b55', borderRadius: '8px', padding: '10px', fontWeight: 'bold', fontSize: '0.9rem', cursor: 'pointer' }}>
                  Editar equipo
                </button>
              ) : null; })()}
            </div>
          ) : (
            <div style={{ background: '#ff444422', border: '1px solid #ff444444', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
              <p style={{ color: '#ff4444', fontWeight: 'bold', fontSize: '0.9rem', margin: 0 }}>Ventana cerrada</p>
            </div>
          )}
        </div>
      </div>

      <MenuContextual
        menuContextual={menuContextual}
        equipo={equipoLibre}
        posActual={posLibre}
        configSuplentes={CONFIG_SUPLENTES_LIBRE}
        onTransferir={() => {
          setPosicionSeleccionada(menuContextual.pos);
          setIndexAEditar(menuContextual.index);
          setMenuContextual(null);
          setMercadoAbierto(true);
        }}
        onIntercambiar={handleIntercambiarLibre}
        onQuitar={handleQuitarLibre}
        onCerrar={cerrarTodo}
        capitanIndex={capitanIndex}
        onCapitan={handleCapitan}
      />

      {/* ⚠️ NO BORRAR: Toast de notificaciones */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '28px', left: '50%', transform: 'translateX(-50%)',
          background: toast.tipo === 'error' ? '#1a0808' : '#081a08',
          border: `1px solid ${toast.tipo === 'error' ? '#ef444466' : '#22c55e66'}`,
          color: toast.tipo === 'error' ? '#ef4444' : '#22c55e',
          padding: '12px 24px', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 'bold',
          zIndex: 99999, boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
          whiteSpace: 'nowrap',
        }}>
          {toast.tipo === 'error' ? '⚠️ ' : '✓ '}{toast.msg}
        </div>
      )}
      <Tutorial tutorialId="equipo_libre" userId={session?.user?.id} pasos={TUTORIALES.equipo_libre} botonVerDeNuevo={true}/>

      {mostrarConfirmar && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#0f172a', border: '1px solid #a855f744', borderRadius: '14px', padding: '28px 32px', maxWidth: '380px', width: '90%', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '10px' }}></div>
            <h3 style={{ color: '#fff', margin: '0 0 8px', fontSize: '1.1rem' }}>¿Confirmar equipo libre?</h3>
            <p style={{ color: '#94a3b8', fontSize: '0.88rem', margin: '0 0 20px', lineHeight: 1.5 }}>
               <strong style={{ color: '#fff' }}>Recorda que cuando falten 30 minutos para el inicio de la fecha no podras editar el equipo.</strong>
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={() => setMostrarConfirmar(false)}
                style={{ padding: '10px 24px', background: 'transparent', border: '1px solid #334155', borderRadius: '8px', color: '#94a3b8', cursor: 'pointer', fontSize: '0.9rem' }}>
                Cancelar
              </button>
              <button onClick={() => { setMostrarConfirmar(false); confirmarEquipo(); }}
                style={{ padding: '10px 24px', background: '#a855f7', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.9rem' }}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EquipoLibre;